import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Users, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatINR } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const VendorsPage = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    name: '',
    category: '',
    contact_person: '',
    phone: '',
    email: '',
    cost: '',
    notes: ''
  });

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/vendors`, { withCredentials: true });
      setVendors(data);
    } catch (error) {
      toast.error('Error fetching vendors');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/vendors`, {
        ...form,
        cost: form.cost ? parseFloat(form.cost) : null
      }, { withCredentials: true });
      toast.success('Vendor added!');
      setDialogOpen(false);
      setForm({ name: '', category: '', contact_person: '', phone: '', email: '', cost: '', notes: '' });
      fetchVendors();
    } catch (error) {
      toast.error('Error adding vendor');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-serif font-bold tracking-tight">Vendors</h1>
          <p className="text-muted-foreground mt-1">Manage your wedding vendors</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="add-vendor-button">
              <Plus className="h-4 w-4 mr-2" />
              Add Vendor
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Vendor</DialogTitle>
              <DialogDescription>Add a vendor for your wedding</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Vendor Name</Label>
                <Input
                  placeholder="e.g., Elite Photography"
                  value={form.name}
                  onChange={(e) => setForm({...form, name: e.target.value})}
                  required
                  data-testid="vendor-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Input
                  placeholder="e.g., Photography, Catering, Decoration"
                  value={form.category}
                  onChange={(e) => setForm({...form, category: e.target.value})}
                  required
                  data-testid="vendor-category-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Contact Person</Label>
                <Input
                  placeholder="Contact person name"
                  value={form.contact_person}
                  onChange={(e) => setForm({...form, contact_person: e.target.value})}
                  data-testid="vendor-contact-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    type="tel"
                    placeholder="Phone number"
                    value={form.phone}
                    onChange={(e) => setForm({...form, phone: e.target.value})}
                    data-testid="vendor-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    placeholder="vendor@email.com"
                    value={form.email}
                    onChange={(e) => setForm({...form, email: e.target.value})}
                    data-testid="vendor-email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Cost (₹)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={form.cost}
                  onChange={(e) => setForm({...form, cost: e.target.value})}
                  data-testid="vendor-cost-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  placeholder="Additional notes..."
                  value={form.notes}
                  onChange={(e) => setForm({...form, notes: e.target.value})}
                  data-testid="vendor-notes-input"
                />
              </div>
              <Button type="submit" className="w-full rounded-full" data-testid="submit-vendor-button">
                Add Vendor
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {vendors.length === 0 ? (
        <Card className="shadow-sm">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">No vendors yet. Add your first vendor!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {vendors.map((vendor) => (
            <Card key={vendor.id} className="shadow-sm hover:shadow-md transition-shadow" data-testid="vendor-card">
              <CardContent className="p-6">
                <div className="space-y-3">
                  <div>
                    <h3 className="font-serif text-lg font-semibold">{vendor.name}</h3>
                    <p className="text-sm text-muted-foreground uppercase tracking-wider">{vendor.category}</p>
                  </div>
                  {vendor.contact_person && (
                    <p className="text-sm"><span className="text-muted-foreground">Contact:</span> {vendor.contact_person}</p>
                  )}
                  {vendor.phone && (
                    <p className="text-sm"><span className="text-muted-foreground">Phone:</span> {vendor.phone}</p>
                  )}
                  {vendor.email && (
                    <p className="text-sm"><span className="text-muted-foreground">Email:</span> {vendor.email}</p>
                  )}
                  {vendor.cost && (
                    <p className="text-sm font-medium text-primary">{formatINR(vendor.cost)}</p>
                  )}
                  {vendor.notes && (
                    <p className="text-sm text-muted-foreground border-t pt-3 mt-3">{vendor.notes}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};